import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { getTenantConfig } from '@/lib/tenant-config'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { Button } from '@lead-magnifier/ui'

export const Route = createFileRoute('/messetasche/bestaetigen/$doiToken/')({
  component: DoiConfirmPage,
})

function DoiConfirmPage() {
  const { doiToken } = Route.useParams()
  const tenant = getTenantConfig()
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading')
  const [visitorToken, setVisitorToken] = useState('')
  const [eventName, setEventName] = useState('')

  useEffect(() => {
    confirmDoi()
  }, [doiToken])

  async function confirmDoi() {
    try {
      // Visitor über DOI-Token finden
      const { data: visitor, error } = await supabase
        .from('visitors')
        .select('id, token, doi_confirmed, events (name)')
        .eq('doi_token', doiToken)
        .single()

      if (error || !visitor) {
        setState('error')
        return
      }

      const event = visitor.events as unknown as { name: string }
      setEventName(event.name)
      setVisitorToken(visitor.token)

      // Bereits bestätigt?
      if (visitor.doi_confirmed) {
        setState('success')
        return
      }

      // Bestätigen
      const { error: updateError } = await supabase
        .from('visitors')
        .update({ doi_confirmed: true, doi_confirmed_at: new Date().toISOString() })
        .eq('id', visitor.id)

      if (updateError) throw updateError

      setState('success')
    } catch {
      setState('error')
    }
  }

  const brandColor = tenant.branding.primary_color

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
      <div className="max-w-sm w-full text-center">

        {state === 'loading' && (
          <div>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-neutral-400" />
            <p className="mt-4 text-neutral-500">E-Mail wird bestätigt...</p>
          </div>
        )}

        {state === 'success' && (
          <div className="animate-fade-in-up">
            <CheckCircle2 className="h-16 w-16 mx-auto mb-4" style={{ color: brandColor }} />
            <h1 className="text-2xl font-bold">E-Mail bestätigt!</h1>
            <p className="text-neutral-500 mt-2 text-base leading-relaxed">
              Ihre E-Mail-Adresse wurde bestätigt. Sie haben jetzt vollen Zugang zu allen Kontaktdaten und Dokumenten
              {eventName && <> der <strong>{eventName}</strong></>}.
            </p>
            {visitorToken && (
              <a href={`/messetasche/${visitorToken}`} className="block mt-6">
                <Button className="w-full h-12 rounded-md font-bold" style={{ backgroundColor: brandColor }}>
                  Meine Messetasche öffnen
                </Button>
              </a>
            )}
          </div>
        )}

        {state === 'error' && (
          <div className="animate-fade-in-up">
            <XCircle className="h-16 w-16 mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-bold">Link ungültig</h1>
            <p className="text-neutral-500 mt-2">
              Dieser Bestätigungslink ist ungültig oder abgelaufen.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
